<template>
  <div class="app-container seal-stock-page seal-board-page">
    <div v-if="fatalError" class="board-error">{{ fatalError }}</div>
    <template v-else>
      <div class="head-container stock-filter-bar">
        <el-select
          v-model="stockQuery.ruleId"
          clearable
          filterable
          size="small"
          placeholder="触点规则"
          class="stock-filter-item"
          @change="onStockFilterChange"
        >
          <el-option
            v-for="c in ruleOptions"
            :key="c.id"
            :label="c.ruleName + ' (' + c.ruleCode + ')'"
            :value="c.id"
          />
        </el-select>
        <el-select
          v-model="stockQuery.baseId"
          clearable
          filterable
          size="small"
          placeholder="印章底部"
          class="stock-filter-item"
          @change="onStockFilterChange"
        >
          <el-option
            v-for="b in baseOptions"
            :key="b.id"
            :label="b.baseName + ' (' + b.baseCode + ')'"
            :value="b.id"
          />
        </el-select>
        <el-select
          v-model="stockQuery.handleId"
          clearable
          filterable
          size="small"
          placeholder="印章手柄"
          class="stock-filter-item"
          @change="onStockFilterChange"
        >
          <el-option
            v-for="h in handleOptions"
            :key="h.id"
            :label="h.handleName"
            :value="h.id"
          />
        </el-select>
        <el-button type="primary" icon="el-icon-search" size="small" @click="searchStockList">查询</el-button>
      </div>

      <el-table
        v-loading="stockLoading"
        :data="stockList"
        size="small"
        border
        stripe
        highlight-current-row
        style="width:100%;margin-top:12px;"
      >
        <el-table-column label="物料编码" min-width="168" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-button type="text" class="sku-link" @click="openSkuDetail(scope.row)">
              {{ scope.row.skuCode || '—' }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="底座图" width="72" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="toFullUrl(scope.row.appearanceImage)"
              :src="toFullUrl(scope.row.appearanceImage)"
              :preview-src-list="[toFullUrl(scope.row.appearanceImage)]"
              fit="contain"
              class="thumb-img"
            />
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="底座信息" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <template v-if="scope.row.baseId && scope.row.baseName">
              <div>{{ scope.row.baseName }}</div>
              <div class="sub-line">
                <span v-if="scope.row.color">{{ scope.row.color }}</span>
                <span v-if="scope.row.bottomSizeText"> · {{ scope.row.bottomSizeText }}</span>
              </div>
            </template>
            <el-tag v-else type="warning" size="mini">未绑定底座</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
        <el-table-column label="手柄图" width="72" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="toFullUrl(scope.row.handleImage)"
              :src="toFullUrl(scope.row.handleImage)"
              :preview-src-list="[toFullUrl(scope.row.handleImage)]"
              fit="contain"
              class="thumb-img"
            />
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="手柄信息" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>{{ scope.row.handleName || '—' }}</div>
            <div v-if="scope.row.handleColor" class="sub-line">{{ scope.row.handleColor }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="stockQty" label="当前库存" width="96" align="center">
          <template slot-scope="scope">
            <span :class="['qty-num', (scope.row.stockQty || 0) <= 0 ? 'is-empty' : '']">
              {{ scope.row.stockQty != null ? scope.row.stockQty : 0 }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="96" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="openSkuDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        style="margin-top: 16px; text-align: right;"
        background
        layout="total, prev, pager, next, sizes"
        :current-page="stockQuery.page + 1"
        :page-size="stockQuery.size"
        :page-sizes="[10, 20, 50]"
        :total="stockTotal"
        @current-change="(p) => { stockQuery.page = Math.max(p - 1, 0); loadStockList() }"
        @size-change="(s) => { stockQuery.size = s; stockQuery.page = 0; loadStockList() }"
      />
    </template>

    <el-dialog
      :title="movementsTitle"
      :visible.sync="movementsVisible"
      width="960px"
      top="6vh"
      @closed="resetMovements"
    >
      <div v-loading="movementsLoading">
        <el-descriptions v-if="movementsSku" :column="3" size="small" border style="margin-bottom:14px;">
          <el-descriptions-item label="物料编码">{{ movementsSku.skuCode || '—' }}</el-descriptions-item>
          <el-descriptions-item label="底座">{{ movementsSku.baseName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="手柄">{{ movementsSku.handleName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="当前库存">{{ movementsSku.stockQty != null ? movementsSku.stockQty : 0 }}</el-descriptions-item>
          <el-descriptions-item label="触点规则">{{ movementsSku.ruleName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="名称">{{ movementsSku.skuName || '—' }}</el-descriptions-item>
        </el-descriptions>
        <div class="movements-block">
          <div class="movements-title">入库记录（{{ movementsInList.length }}）</div>
          <el-table :data="movementsInList" size="small" border stripe max-height="280">
            <el-table-column prop="batchNo" label="入库批次" min-width="150" show-overflow-tooltip />
            <el-table-column prop="supplierName" label="供应商" min-width="110" show-overflow-tooltip />
            <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
            <el-table-column prop="baseName" label="底座" min-width="110" show-overflow-tooltip />
            <el-table-column prop="handleName" label="手柄" width="100" show-overflow-tooltip />
            <el-table-column prop="qty" label="数量" width="72" align="center" />
            <el-table-column label="进货价" width="96" align="right">
              <template slot-scope="scope">
                <span v-if="scope.row.purchasePrice != null">{{ Number(scope.row.purchasePrice).toFixed(4) }}</span>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="时间" width="160" />
          </el-table>
          <div v-if="!movementsInList.length" class="empty-tip">暂无入库记录</div>
        </div>
        <div class="movements-block">
          <div class="movements-title">出库记录（{{ movementsOutList.length }}）</div>
          <el-table :data="movementsOutList" size="small" border stripe max-height="280">
            <el-table-column prop="batchNo" label="出库批次" min-width="150" show-overflow-tooltip />
            <el-table-column prop="orderNo" label="订单号" min-width="110" show-overflow-tooltip />
            <el-table-column label="渠道" width="88" align="center">
              <template slot-scope="scope">{{ channelLabel(scope.row.channel) }}</template>
            </el-table-column>
            <el-table-column prop="baseName" label="底座" min-width="110" show-overflow-tooltip />
            <el-table-column prop="handleName" label="手柄" width="100" show-overflow-tooltip />
            <el-table-column prop="qty" label="数量" width="72" align="center" />
            <el-table-column prop="createTime" label="时间" width="160" />
          </el-table>
          <div v-if="!movementsOutList.length" class="empty-tip">暂无出库记录</div>
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="movementsVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchPublicSealStockBoard,
  fetchPublicSealStockOptions,
  fetchPublicSealSkuMovements
} from '@/api/seal-stock-public'
import '../admin/admin-pc.less'

const ADMIN_PC_CLASS = 'admin-pc-mode'

export default {
  name: 'SealStockBoardPublic',
  data() {
    return {
      token: '',
      fatalError: '',
      ruleOptions: [],
      baseOptions: [],
      handleOptions: [],
      channels: [],
      stockLoading: false,
      stockList: [],
      stockTotal: 0,
      stockQuery: { page: 0, size: 10, ruleId: null, baseId: null, handleId: null },
      movementsVisible: false,
      movementsLoading: false,
      movementsTitle: '物料入出库记录',
      movementsSku: null,
      movementsInList: [],
      movementsOutList: []
    }
  },
  created() {
    document.documentElement.classList.add(ADMIN_PC_CLASS)
    document.body.style.overflow = 'auto'
    this.token = String((this.$route.query && this.$route.query.token) || '').trim()
    if (!this.token) {
      this.fatalError = '链接无效：缺少访问参数 token'
      return
    }
    this.loadOptions()
    this.loadStockList()
  },
  beforeDestroy() {
    document.documentElement.classList.remove(ADMIN_PC_CLASS)
    document.body.style.overflow = ''
  },
  methods: {
    toFullUrl(url) {
      if (!url) return ''
      const s = String(url).trim()
      if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('data:')) return s
      if (s.indexOf('/upload/') === 0 || s.indexOf('upload/') === 0) {
        return `https://oss-pai-s2lxmtw6mbk6ofo9ye-cn-shanghai.oss-cn-shanghai.aliyuncs.com/upload/${s.replace(/^\/?upload\/?/, '')}`
      }
      return s
    },
    channelLabel(code) {
      const hit = (this.channels || []).find((c) => c.code === code)
      return hit ? hit.label : (code || '—')
    },
    loadOptions() {
      fetchPublicSealStockOptions(this.token)
        .then((data) => {
          this.ruleOptions = (data && data.rules) || []
          this.baseOptions = (data && data.bases) || []
          this.handleOptions = (data && data.handles) || []
          this.channels = (data && data.channels) || []
        })
        .catch((e) => {
          const msg = (e && e.message) || '加载筛选条件失败'
          if (String(msg).indexOf('令牌') >= 0) {
            this.fatalError = msg
          } else {
            this.$message.error(msg)
          }
        })
    },
    onStockFilterChange() {
      this.stockQuery.page = 0
      this.loadStockList()
    },
    searchStockList() {
      this.stockQuery.page = 0
      this.loadStockList()
    },
    loadStockList() {
      this.stockLoading = true
      fetchPublicSealStockBoard({
        token: this.token,
        page: this.stockQuery.page,
        size: this.stockQuery.size,
        ruleId: this.stockQuery.ruleId || undefined,
        baseId: this.stockQuery.baseId || undefined,
        handleId: this.stockQuery.handleId || undefined
      })
        .then((data) => {
          this.stockList = (data && data.content) || []
          this.stockTotal = (data && data.totalElements) || 0
        })
        .catch((e) => {
          this.stockList = []
          this.stockTotal = 0
          const msg = (e && e.message) || '加载失败'
          if (String(msg).indexOf('令牌') >= 0) {
            this.fatalError = msg
          } else {
            this.$message.error(msg)
          }
        })
        .finally(() => {
          this.stockLoading = false
        })
    },
    openSkuDetail(row) {
      if (!row || !row.id) return
      this.movementsVisible = true
      this.movementsLoading = true
      this.movementsTitle = '物料入出库记录 · ' + (row.skuCode || row.id)
      this.movementsSku = { ...row }
      fetchPublicSealSkuMovements(this.token, row.id)
        .then((data) => {
          if (data && data.sku) {
            this.movementsSku = { ...this.movementsSku, ...data.sku }
          }
          this.movementsInList = (data && data.inList) || []
          this.movementsOutList = (data && data.outList) || []
        })
        .catch(() => {
          this.movementsInList = []
          this.movementsOutList = []
          this.$message.error('加载入出库记录失败')
        })
        .finally(() => {
          this.movementsLoading = false
        })
    },
    resetMovements() {
      this.movementsSku = null
      this.movementsInList = []
      this.movementsOutList = []
      this.movementsTitle = '物料入出库记录'
    }
  }
}
</script>

<style scoped>
.seal-board-page {
  min-height: 100vh;
  background: #fff;
}
.seal-board-page .board-error {
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #fbc4c4;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 14px;
}
.seal-stock-page .stock-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.seal-stock-page .stock-filter-item {
  width: 168px;
}
.seal-stock-page .thumb-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}
.seal-stock-page .sku-link {
  padding: 0;
  font-weight: 600;
}
.seal-stock-page .sub-line {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.3;
}
.seal-stock-page .muted {
  color: #c0c4cc;
}
.seal-stock-page .qty-num {
  font-weight: 700;
  color: #303133;
}
.seal-stock-page .qty-num.is-empty {
  color: #f56c6c;
}
.seal-stock-page .movements-block {
  margin-bottom: 16px;
}
.seal-stock-page .movements-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #303133;
}
.seal-stock-page .empty-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
</style>
